import { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router";
import Select from "react-select";
import { publicRequest, userRequest } from "../../requestMethods";
import { FiPlus, FiTrash2 } from "react-icons/fi";

const DEFAULT_PRODUCT_ID = "6729fd0383847c23cbce81b2";

const statusOptions = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
];

const typeOptions = [
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Half-Yearly", value: "half-yearly" },
    { label: "Yearly", value: "yearly" },
];

export default function NewTerms() {
    const history = useHistory();
    const { id } = useParams();
    const isEdit = !!id;

    const [usersData, setUsersData] = useState([]);
    const [inputs, setInputs] = useState({
        userId: "",
        date: new Date().toISOString().slice(0, 10),
        status: "active",
        type: "monthly",
    });
    const [products, setProducts] = useState([
        {
            productId: DEFAULT_PRODUCT_ID,
            code: "",
            itemCode: "",
            price: 0,
            quantity: 1,
            amount: 0,
        },
    ]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await publicRequest.get("/users/all");
                setUsersData(res?.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        if (isEdit) {
            const fetchTerms = async () => {
                try {
                    const res = await publicRequest.get(`/terms/${id}`);
                    const t = res.data;
                    setInputs({
                        userId: t.userId?._id || t.userId || "",
                        date: t.date
                            ? new Date(t.date).toISOString().slice(0, 10)
                            : new Date().toISOString().slice(0, 10),
                        status: t.status || "active",
                        type: t.type || "monthly",
                    });
                    setProducts(
                        Array.isArray(t.products) && t.products.length > 0
                            ? t.products.map((p) => ({
                                  productId: p.productId?._id || p.productId,
                                  code: p.code || p.productId?.model || p.productId?.title || "",
                                  itemCode: p.itemCode || "",
                                  price: p.price ?? 0,
                                  quantity: p.quantity ?? 1,
                                  amount: p.amount ?? (p.price || 0) * (p.quantity || 1),
                              }))
                            : [
                                  {
                                      productId: DEFAULT_PRODUCT_ID,
                                      code: "",
                                      itemCode: "",
                                      price: 0,
                                      quantity: 1,
                                      amount: 0,
                                  },
                              ]
                    );
                } catch (err) {
                    console.error(err);
                }
            };
            fetchTerms();
        }
    }, [id, isEdit]);

    const handleChange = (e) => {
        setInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSelectChange = (field, opt) => {
        setInputs((prev) => ({ ...prev, [field]: opt?.value ?? "" }));
    };

    const addProductRow = () => {
        setProducts((prev) => [
            ...prev,
            {
                productId: DEFAULT_PRODUCT_ID,
                code: "",
                itemCode: "",
                price: 0,
                quantity: 1,
                amount: 0,
            },
        ]);
    };

    const removeProductRow = (idx) => {
        setProducts((prev) => prev.filter((_, i) => i !== idx));
    };

    const updateProduct = (idx, field, value) => {
        setProducts((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            if (field === "price" || field === "quantity") {
                const p = next[idx].price ?? 0;
                const q = next[idx].quantity ?? 1;
                next[idx].amount = p * q;
            }
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputs.userId) {
            alert("Please select a user");
            return;
        }
        if (!inputs.type) {
            alert("Please select a type");
            return;
        }
        const payload = {
            userId: inputs.userId,
            date: inputs.date,
            status: inputs.status,
            type: inputs.type,
            products: products
                .filter((p) => p.code || p.itemCode || Number(p.price) > 0 || Number(p.quantity) > 0)
                .map((p) => ({
                    productId: p.productId || DEFAULT_PRODUCT_ID,
                    code: p.code || "",
                    itemCode: p.itemCode || "",
                    price: Number(p.price) || 0,
                    quantity: Number(p.quantity) || 1,
                    amount: Number(p.amount) || Number(p.price) * (Number(p.quantity) || 1),
                })),
        };
        try {
            if (isEdit) {
                await userRequest.put(`/terms/${id}`, payload);
                alert("Terms updated successfully");
            } else {
                await userRequest.post("/terms", payload);
                alert("Terms created successfully");
            }
            history.push("/terms");
        } catch (err) {
            console.error(err);
            alert("Failed to save terms");
        }
    };

    const userOptions = usersData.map((u) => ({
        label: u.name || u.email || u._id,
        value: u._id,
    }));

    return (
        <div className="newProduct max-w-4xl mx-auto p-6">
            <h1 className="addProductTitle mb-6">
                {isEdit ? "Edit Terms" : "New Terms"}
            </h1>
            <form className="addProductForm" onSubmit={handleSubmit}>
                <div className="addProductItem mb-4">
                    <label className="black required">User</label>
                    <Select
                        options={userOptions}
                        value={userOptions.find((o) => o.value === inputs.userId)}
                        onChange={(opt) => handleSelectChange("userId", opt)}
                        placeholder="Select user"
                    />
                </div>
                <div className="addProductItem mb-4">
                    <label className="black">Date</label>
                    <input
                        name="date"
                        type="date"
                        value={inputs.date}
                        onChange={handleChange}
                        className="w-full p-2 border rounded"
                    />
                </div>
                <div className="addProductItem mb-4">
                    <label className="black">Status</label>
                    <Select
                        options={statusOptions}
                        value={statusOptions.find((o) => o.value === inputs.status)}
                        onChange={(opt) => handleSelectChange("status", opt)}
                    />
                </div>
                <div className="addProductItem mb-4">
                    <label className="black required">Type</label>
                    <Select
                        options={typeOptions}
                        value={typeOptions.find((o) => o.value === inputs.type)}
                        onChange={(opt) => handleSelectChange("type", opt)}
                    />
                </div>
                <div className="addProductItem mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <label className="black">Products (code, price, quantity)</label>
                        <button
                            type="button"
                            onClick={addProductRow}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                        >
                            <FiPlus /> Add
                        </button>
                    </div>
                    <div className="border rounded overflow-x-auto">
                        <table className="w-full text-sm table-fixed">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="p-2 text-left w-auto">Code</th>
                                    <th className="p-2 text-right w-28">Price</th>
                                    <th className="p-2 text-right w-24">Qty</th>
                                    <th className="p-2 text-right w-24">Amount</th>
                                    <th className="p-2 w-12"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p, idx) => (
                                    <tr key={idx} className="border-t">
                                        <td className="p-2">
                                            <input
                                                type="text"
                                                value={p.code || p.itemCode || ""}
                                                onChange={(e) =>
                                                    updateProduct(
                                                        idx,
                                                        "code",
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Code"
                                                className="w-full p-1 border rounded"
                                            />
                                        </td>
                                        <td className="p-2 text-right">
                                            <input
                                                type="number"
                                                value={p.price}
                                                onChange={(e) =>
                                                    updateProduct(
                                                        idx,
                                                        "price",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full min-w-[5rem] p-1 border rounded text-right"
                                            />
                                        </td>
                                        <td className="p-2 text-right">
                                            <input
                                                type="number"
                                                value={p.quantity}
                                                onChange={(e) =>
                                                    updateProduct(
                                                        idx,
                                                        "quantity",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full min-w-[4rem] p-1 border rounded text-right"
                                            />
                                        </td>
                                        <td className="p-2 text-right">
                                            {p.amount ?? p.price * (p.quantity || 1)}
                                        </td>
                                        <td className="p-2">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeProductRow(idx)
                                                }
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="flex gap-4 mt-6">
                    <button
                        type="submit"
                        className="updateProductButton"
                    >
                        {isEdit ? "Update" : "Create"}
                    </button>
                    <button
                        type="button"
                        onClick={() => history.push("/terms")}
                        className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}
